import { BaseSchema } from 'yup';
import type { AnySchema } from 'yup';

import Yup from '@tmh-website/services/validation.service';
import { assertEnvVariableExists } from '@tmh-website/shared/helpers';
import { StaticFormVariant } from '@tmh-website/services/form.service';
import * as api from '@tmh-website/services/electricity-tariff/electricity-tariff.client.service';

export const baseValidation = Yup.object({
  debug: Yup.boolean(),
  debugEmail: Yup.string(),
  gRecaptchaResponse: Yup.string().required(),
});

export type FieldName =
  | 'first_name'
  | 'last_name'
  | 'postal_code'
  | 'street'
  | 'city'
  | 'company'
  | 'phone'
  | 'email'
  | 'customer_type'
  | 'issue_type'
  | 'manufacturer'
  | 'serial_number'
  | 'site_name'
  | 'old_serial_number'
  | 'new_serial_number'
  | 'station_online'
  | 'wallbox_id'
  | 'charging_issue_description'
  | 'charging_issue_error'
  | 'vehicle_name'
  | 'rfid_card_id'
  | 'actions_description'
  | 'customer_number'
  | 'order_number'
  | 'message';

export type W2CValues = Record<FieldName, string> & Yup.InferType<typeof baseValidation>;

export const initialValues: W2CValues = {
  debug: false,
  debugEmail: 'webpresence-team@codetain.com',
  gRecaptchaResponse: '',
  first_name: '',
  last_name: '',
  postal_code: '',
  street: '',
  city: '',
  company: '',
  phone: '',
  email: '',
  customer_type: '',
  issue_type: '',
  manufacturer: '',
  serial_number: '',
  site_name: '',
  old_serial_number: '',
  new_serial_number: '',
  station_online: '',
  wallbox_id: '',
  charging_issue_description: '',
  charging_issue_error: '',
  vehicle_name: '',
  rfid_card_id: '',
  actions_description: '',
  customer_number: '',
  order_number: '',
  message: '',
};

export type W2CFieldLabels = Record<FieldName, string>;

export interface W2CPayloadValues {
  captcha_settings: string;
  orgid: string;
  retURL: string;
  description: string;
  subject: string;
  name: string;
  type: string;
  reason: string;
  priority: string;
  company?: string;
  phone: string;
  email: string;
  CustomerType__c: string;
  'g-recaptcha-response': string;
  submit: string;
  debugEmail?: string;
  debug?: 1;
}

export type Web2CaseStaticFormVariant =
  | StaticFormVariant.Web2Case
  | StaticFormVariant.ETWeb2CaseSupport
  | StaticFormVariant.ETWeb2CaseContact;

/**
 * This function acts as a single source of truth for the dynamic fields of the Web2Case form.
 * The visibility and validation of each field depends on the form variant and on the value of the field `issue_type`.
 * The validation is only relevant if the field is visible.
 */
export function getDynamicFormConfig(
  values: W2CValues,
  formVariant: Web2CaseStaticFormVariant
): Record<FieldName, { visible: boolean; validation: AnySchema }> {
  return {
    first_name: {
      visible: true,
      validation: Yup.string().required(),
    },
    last_name: {
      visible: true,
      validation: Yup.string().required(),
    },
    postal_code: {
      visible: formVariant === StaticFormVariant.Web2Case,
      validation: Yup.string().required(),
    },
    street: {
      visible: formVariant === StaticFormVariant.Web2Case,
      validation: Yup.string().required(),
    },
    city: {
      visible: formVariant === StaticFormVariant.Web2Case,
      validation: Yup.string().required(),
    },
    company: {
      visible: formVariant === StaticFormVariant.Web2Case,
      validation: Yup.string(),
    },
    phone: {
      visible: true,
      validation: formVariant === StaticFormVariant.Web2Case ? Yup.string().required() : Yup.string(),
    },
    email: {
      visible: true,
      validation: Yup.string().email().required(),
    },
    customer_type: {
      visible: formVariant === StaticFormVariant.Web2Case,
      validation: Yup.string().required(),
    },
    issue_type: {
      visible: true,
      validation: Yup.string().required(),
    },
    manufacturer: {
      visible:
        formVariant === StaticFormVariant.Web2Case &&
        ['wallbox', 'chargeWallbox', 'chargePilotBill'].includes(values['issue_type']),
      validation:
        formVariant === StaticFormVariant.Web2Case && ['wallbox', 'chargeWallbox'].includes(values['issue_type'])
          ? Yup.string().required()
          : Yup.string(),
    },
    serial_number: {
      visible:
        formVariant === StaticFormVariant.Web2Case &&
        ['wallbox', 'chargeWallbox', 'chargePilotBill'].includes(values['issue_type']),
      validation:
        formVariant === StaticFormVariant.Web2Case && ['wallbox', 'chargeWallbox'].includes(values['issue_type'])
          ? Yup.string().required()
          : Yup.string(),
    },
    site_name: {
      visible:
        formVariant === StaticFormVariant.Web2Case &&
        ['chargePilotCharging', 'chargePilotExchange', 'chargePilotWeb'].includes(values['issue_type']),
      validation: Yup.string().required(),
    },
    old_serial_number: {
      visible: formVariant === StaticFormVariant.Web2Case && values['issue_type'] === 'chargePilotExchange',
      validation: Yup.string().required(),
    },
    new_serial_number: {
      visible: formVariant === StaticFormVariant.Web2Case && values['issue_type'] === 'chargePilotExchange',
      validation: Yup.string().required(),
    },
    station_online: {
      visible: formVariant === StaticFormVariant.Web2Case && values['issue_type'] === 'chargePilotExchange',
      validation: Yup.string().required(),
    },
    wallbox_id: {
      visible: formVariant === StaticFormVariant.Web2Case && values['issue_type'] === 'chargePilotCharging',
      validation: Yup.string().required(),
    },
    charging_issue_description: {
      visible:
        formVariant === StaticFormVariant.Web2Case &&
        ['chargeWallbox', 'chargePilotCharging'].includes(values['issue_type']),
      validation: Yup.string().required(),
    },
    charging_issue_error: {
      visible:
        formVariant === StaticFormVariant.Web2Case &&
        ['chargeWallbox', 'chargePilotCharging'].includes(values['issue_type']),
      validation: Yup.string().required(),
    },
    vehicle_name: {
      visible: formVariant === StaticFormVariant.Web2Case && values['issue_type'] === 'chargePilotCharging',
      validation: Yup.string().required(),
    },
    rfid_card_id: {
      visible: formVariant === StaticFormVariant.Web2Case && values['issue_type'] === 'chargePilotCharging',
      validation: Yup.string().required(),
    },
    actions_description: {
      visible: formVariant === StaticFormVariant.Web2Case && values['issue_type'] === 'chargePilotCharging',
      validation: Yup.string().required(),
    },
    customer_number: {
      visible:
        (formVariant === StaticFormVariant.Web2Case && values['issue_type'] === 'chargePilotCharging') ||
        (formVariant === StaticFormVariant.ETWeb2CaseSupport &&
          ['contract', 'invoice', 'intelligentMeteringSystem'].includes(values['issue_type'])),
      validation: Yup.string(),
    },
    order_number: {
      visible: formVariant === StaticFormVariant.Web2Case && values['issue_type'] === 'complaint',
      validation: Yup.string().required(),
    },
    message: {
      visible: true,
      validation:
        formVariant === StaticFormVariant.Web2Case && values['issue_type'] === 'chargePilotExchange'
          ? Yup.string()
          : Yup.string().required(),
    },
  };
}

export function validationIsRequired(validation: BaseSchema) {
  return validation.spec.presence === 'required';
}

const typeAndReasonMapping = {
  chargePilotBill: {
    reason: 'GENERAL_SUPPORT',
    type: 'Charge&amp;Bill',
  },
  chargePilotCharging: {
    reason: 'TECHNICAL_SUPPORT',
    type: 'ChargePilot',
  },
  chargePilotExchange: {
    reason: 'GENERAL_SUPPORT',
    type: 'ChargePilot',
  },
  chargePilotWeb: {
    reason: 'GENERAL_SUPPORT',
    type: 'ChargePilot',
  },
  chargeWallbox: {
    reason: 'TECHNICAL_SUPPORT',
    type: 'Charging Solutions',
  },
  complaint: {
    reason: 'COMPLAINT',
    type: '',
  },
  ghg: {
    reason: 'GENERAL_SUPPORT',
    type: 'THG Quote',
  },
  other: {
    reason: '',
    type: '',
  },
  wallbox: {
    reason: 'GENERAL_SUPPORT',
    type: 'Charging Solutions',
  },
};

const typeAndReasonMappingET = {
  app: {
    reason: 'GENERAL_SUPPORT',
    type: 'VGI Customer',
  },
  contract: {
    reason: 'CONTRACT_INVOICE',
    type: 'VGI Customer',
  },
  flexCoins: {
    reason: 'TECHNICAL_SUPPORT',
    type: 'VGI Customer',
  },
  intelligentMeteringSystem: {
    reason: 'TECHNICAL_SUPPORT',
    type: 'VGI Customer',
  },
  invoice: {
    reason: 'CONTRACT_INVOICE',
    type: 'VGI Customer',
  },
  other: {
    reason: '',
    type: '',
  },
  v2gHours: {
    reason: 'TECHNICAL_SUPPORT',
    type: 'VGI Customer',
  },
};

function formatField(name: FieldName, fieldLabels: Record<string, string>, values: W2CValues) {
  return fieldLabels[name] + ': ' + values[name] + '\n\n';
}

/**
 * Resolves the value for the `captcha_settings` property of the Web2Case payload object.
 *
 * @returns - The value for the `captcha_settings` property of the Web2Case payload object.
 */
const resolveWeb2CasePayloadCaptchaSettings = (): W2CPayloadValues['captcha_settings'] => {
  assertEnvVariableExists('NEXT_PUBLIC_SALESFORCE_W2C_ORG', process.env.NEXT_PUBLIC_SALESFORCE_W2C_ORG);

  return JSON.stringify({
    keyname: 'reCAPTCHA_v2_apollo',
    fallback: 'true',
    orgId: process.env.NEXT_PUBLIC_SALESFORCE_W2C_ORG,
    ts: `${new Date().getTime()}`,
  });
};

/**
 * Resolves the value for the `CustomerType__c` property of the Web2Case payload object.
 *
 * @param values - Formik's form values.
 * @param formVariant - The current form variant.
 *
 * @returns - The value for the `CustomerType__c` property of the Web2Case payload object.
 */
const resolveWeb2CasePayloadCustomerType = (
  values: W2CValues,
  formVariant: Web2CaseStaticFormVariant
): W2CPayloadValues['CustomerType__c'] => {
  return formVariant === StaticFormVariant.ETWeb2CaseContact || formVariant === StaticFormVariant.ETWeb2CaseSupport
    ? 'Private'
    : values.customer_type;
};

/**
 * Resolves the value for the `orgid` property of the Web2Case payload object.
 *
 * @returns - The value for the `orgid` property of the Web2Case payload object.
 */
const resolveWeb2CasePayloadOrgId = (): W2CPayloadValues['orgid'] => {
  assertEnvVariableExists('NEXT_PUBLIC_SALESFORCE_W2C_ORG', process.env.NEXT_PUBLIC_SALESFORCE_W2C_ORG);
  return process.env.NEXT_PUBLIC_SALESFORCE_W2C_ORG;
};

/**
 * Resolves the value for the `retURL` property of the Web2Case payload object.
 *
 * @returns - The value for the `retURL` property of the Web2Case payload object.
 */
const resolveWeb2CasePayloadRetUrl = (): W2CPayloadValues['retURL'] => {
  if (typeof window !== 'undefined') {
    return window.location.href;
  } else {
    assertEnvVariableExists('NEXT_PUBLIC_ORIGIN', process.env.NEXT_PUBLIC_ORIGIN);
    return process.env.NEXT_PUBLIC_ORIGIN;
  }
};

/**
 * Resolves the value for the `subject` property of the Web2Case payload object.
 *
 * @returns - The value for the `subject` property of the Web2Case payload object.
 */
const resolveWeb2CasePayloadSubject = (issueType: W2CValues['issue_type']): W2CPayloadValues['subject'] => {
  let subject: string = 'W2C ' + issueType;

  if (typeof window !== 'undefined') {
    const currentUrl: URL = new URL(window.location.href);
    subject = `${subject} (${currentUrl.hostname})`;
  }

  return subject;
};

export function getWeb2CasePayload(
  values: W2CValues,
  fieldLabels: Record<string, string>,
  options: { formVariant: Web2CaseStaticFormVariant }
) {
  const issueType = values['issue_type'] as string;

  const formConfig = getDynamicFormConfig(values, options.formVariant);

  let description = formatField('customer_type', fieldLabels, values);
  description += formatField('issue_type', fieldLabels, values);
  Object.entries(formConfig).forEach(([fieldName, fieldConfig]) => {
    if (fieldConfig.visible) {
      description += formatField(fieldName as FieldName, fieldLabels, values);
    }
  });
  description += `${values.street}\n${values.postal_code} ${values.city}`;

  const typeAndReasonMap =
    options.formVariant === StaticFormVariant.ETWeb2CaseSupport ||
    options.formVariant === StaticFormVariant.ETWeb2CaseContact
      ? typeAndReasonMappingET
      : typeAndReasonMapping;

  const payload: W2CPayloadValues = {
    captcha_settings: resolveWeb2CasePayloadCaptchaSettings(),
    company: values.company ?? undefined,
    CustomerType__c: resolveWeb2CasePayloadCustomerType(values, options.formVariant),
    description: description,
    email: values.email,
    'g-recaptcha-response': values.gRecaptchaResponse ?? '',
    name: values.first_name + ' ' + values.last_name,
    orgid: resolveWeb2CasePayloadOrgId(),
    phone: values.phone,
    priority: 'Low',
    reason: typeAndReasonMap[issueType].reason ?? '',
    retURL: resolveWeb2CasePayloadRetUrl(),
    subject: resolveWeb2CasePayloadSubject(issueType),
    submit: fieldLabels.submit,
    type: typeAndReasonMap[issueType].type ?? '',
  };

  if (values.debug) {
    payload.debug = 1;
    payload.debugEmail = values.debugEmail;
  }

  return payload;
}
